import type { HexColorString } from '@fluent-theme-tools/color-operations';
import {
  ThemeMode,
  checkFluentV9ThemeContrast,
  createTheme,
  getAccessibleBrandRamp,
  getExactBrandRamp,
} from '@fluent-theme-tools/color-operations';
import type { Theme } from '@fluentui/react-components';

import { isValidHexColor } from '../../colors';

import { createCoreUIFluentTheme } from './createCoreUIFluentTheme';

export { ThemeMode };

/**
 * Converts a valid hex color string to HexColorString format
 * Ensures the hex color has a # prefix and is in the correct format
 * Normalizes 3-digit hex colors to 6-digit format for consistency
 * @param color - Hex color string (with or without # prefix)
 * @returns HexColorString in proper format
 * @throws Error if the color format is invalid
 */
const toHexColorString = (color: string): HexColorString => {
  // Add # prefix if missing
  const normalizedColor = color.startsWith('#') ? color : `#${color}`;

  if (!isValidHexColor(normalizedColor)) {
    throw new Error(`Invalid hex color format: ${color}. Expected format: #000 or #000000 (3 or 6-digit hex)`);
  }

  // Expand 3-digit hex to 6-digit hex for consistency
  if (normalizedColor.length === 4) {
    const [, r, g, b] = normalizedColor;

    return `#${r}${r}${g}${g}${b}${b}` as HexColorString;
  }

  return normalizedColor as HexColorString;
};

type CustomFluent9ThemeType = 'exact' | 'accessible';
type CreateCustomFluent9Theme = (color: string, mode?: ThemeMode, type?: CustomFluent9ThemeType) => Theme;
export const createCustomFluent9Theme: CreateCustomFluent9Theme = (color, mode, type) => {
  let brandRamp;
  const hexColorString = toHexColorString(color);

  const themeMode = mode ?? ThemeMode.Light;
  const themeType = type ?? 'accessible';

  if (themeType === 'exact') {
    brandRamp = getExactBrandRamp(hexColorString);
  } else {
    brandRamp = getAccessibleBrandRamp(hexColorString);
  }

  const theme = createTheme(themeMode, brandRamp);

  return createCoreUIFluentTheme(theme);
};

interface CustomFluent9ThemeWithAccessibilityCheck {
  readonly accessibleDarkTheme: Theme;
  readonly accessibleLightTheme: Theme;
  readonly color: string;
  readonly colorAccessible: string;
  readonly exactDarkTheme: Theme;
  readonly exactLightTheme: Theme;
  readonly hasContrastIssues: boolean;
}
type CreateCustomFluent9ThemeWithAccessibilityCheck = (color: string) => CustomFluent9ThemeWithAccessibilityCheck;
export const createCustomFluent9ThemeWithAccessibilityCheck: CreateCustomFluent9ThemeWithAccessibilityCheck = (
  color
) => {
  const exactLightTheme = createCustomFluent9Theme(color, ThemeMode.Light, 'exact');
  const exactDarkTheme = createCustomFluent9Theme(color, ThemeMode.Dark, 'exact');
  const lightThemeAccessibilityCheck = checkFluentV9ThemeContrast(exactLightTheme);
  const darkThemeAccessibilityCheck = checkFluentV9ThemeContrast(exactDarkTheme);
  const hasContrastIssues = lightThemeAccessibilityCheck.failed.length + darkThemeAccessibilityCheck.failed.length > 0;
  const accessibleLightTheme = hasContrastIssues
    ? createCustomFluent9Theme(color, ThemeMode.Light, 'accessible')
    : exactLightTheme;
  const accessibleDarkTheme = hasContrastIssues
    ? createCustomFluent9Theme(color, ThemeMode.Dark, 'accessible')
    : exactDarkTheme;

  return {
    color: exactLightTheme.colorBrandBackground,
    colorAccessible: accessibleLightTheme.colorBrandBackground,
    exactLightTheme,
    exactDarkTheme,
    accessibleLightTheme: hasContrastIssues ? accessibleLightTheme : exactLightTheme,
    accessibleDarkTheme: hasContrastIssues ? accessibleDarkTheme : exactDarkTheme,
    hasContrastIssues,
  };
};
